import { useMemo } from "react";
import { useFirestore } from "hooks/firestore/core/useFirestore";
import { tokenAutomation } from "hooks/firestore/models/tokenAutomation";
import { tokenAllocation } from "hooks/firestore/models/tokenAllocation";
import useCanViewPromotions from "@/hooks/useCanViewPromotions";
import type { Always } from "types/utilities";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { filterAllocationsForTarget } from "@/features/betslip/utils/filterAllocationsForTarget";
import { buildUsedAllocations } from "@/features/betslip/utils/tokenContext";

type UseTokenAllocationsParams = {
  outcomeIds: string[];
  isMulti: boolean;
};

/**
 * Per-target token allocations (single, SEM, or multi). Subscribes
 * to the same Firestore data the betslip's wrapper hooks already
 * subscribe to — SWR dedupes the underlying reads.
 */
export const useTokenAllocations = ({
  outcomeIds,
  isMulti,
}: UseTokenAllocationsParams) => {
  const { data: automations, isLoading: automationsLoading } = useFirestore(
    "/tokenAutomations",
    tokenAutomation.array(),
  );
  const { data: allocations, isLoading: allocationsLoading } = useFirestore(
    "/users/{userId}/tokenAllocations",
    tokenAllocation.array(),
    { keyPath: "id" },
  );
  const canViewPromos = useCanViewPromotions();
  const isLoading = automationsLoading || allocationsLoading;

  const selections = useBetslipStore((state) => state.selections);
  const stakes = useBetslipStore((state) => state.stakes);
  const promoCash = useBetslipStore((state) => state.promoCash);
  const tokens = useBetslipStore((state) => state.tokens);

  const slipState = useMemo(
    () => ({ selections, stakes, promoCash, tokens }),
    [selections, stakes, promoCash, tokens],
  );

  const usedAllocations = useMemo(
    () => buildUsedAllocations(slipState),
    [slipState],
  );

  const targetSelections = useMemo(() => {
    if (isMulti) {
      return Object.values(selections).filter(
        (selection) => selection.isMultiable,
      );
    }
    return outcomeIds
      .map((id) => selections[id])
      .filter((selection): selection is NonNullable<typeof selection> =>
        Boolean(selection),
      );
  }, [selections, outcomeIds, isMulti]);

  const filteredAllocations = useMemo(() => {
    if (!allocations || !automations || !canViewPromos) return [];
    return filterAllocationsForTarget({
      slipState,
      targetSelections,
      isMulti,
      allocations,
      automations,
    });
  }, [
    allocations,
    automations,
    canViewPromos,
    slipState,
    targetSelections,
    isMulti,
  ]);

  return useMemo(
    () => ({
      isLoading,
      allocations: filteredAllocations,
      automations,
      usedAllocations,
    }),
    [isLoading, filteredAllocations, automations, usedAllocations],
  );
};

export type TokenAllocations = Always<
  ReturnType<typeof useTokenAllocations>["allocations"]
>;
